import { annualLossEventProbability, industryLossMultiplier } from './lossEventConstants';

export interface ExpectedLossView {
  measured: boolean;
  ale: number | null;
  revenueAtRiskRaw: number | null;
  revenueAtRiskPct: number | null;
  tier: string | null;
}

const MAX_REVENUE_AT_RISK_PCT = 0.12;

const NOT_MEASURED: ExpectedLossView = {
  measured: false, ale: null, revenueAtRiskRaw: null, revenueAtRiskPct: null, tier: null,
};

/**
 * What-if ALE on the same formula as the backend: revenue at risk x IRIS annual loss-event
 * probability. `basePct` and `sizeMultiplier` come from the backend expectedLoss block; revenue and
 * industry are what the executive typed. With the recorded inputs this reproduces the backend number.
 */
export function whatIfExpectedLoss(input: {
  annualRevenue: number | null | undefined;
  basePct: number | null | undefined;
  sizeMultiplier: number | null | undefined;
  industry: string | null | undefined;
}): ExpectedLossView {
  const { annualRevenue, basePct, sizeMultiplier, industry } = input;
  if (typeof basePct !== 'number' || !Number.isFinite(basePct)) return { ...NOT_MEASURED };
  if (typeof sizeMultiplier !== 'number' || !Number.isFinite(sizeMultiplier)) return { ...NOT_MEASURED };
  if (typeof industry !== 'string' || !industry.trim()) return { ...NOT_MEASURED }; // missing input, not the 1.0 default
  const prob = annualLossEventProbability(annualRevenue);
  if (!prob || typeof annualRevenue !== 'number') return { ...NOT_MEASURED };

  const pct = Math.min(basePct * industryLossMultiplier(industry) * sizeMultiplier, MAX_REVENUE_AT_RISK_PCT);
  // Floored here, as the backend does before computing the ALE, so the ALE matches.
  const revenueAtRiskRaw = Math.floor(annualRevenue * pct);
  return {
    measured: true,
    ale: Math.floor(revenueAtRiskRaw * prob.probability),
    revenueAtRiskRaw,
    revenueAtRiskPct: pct,
    tier: prob.tier,
  };
}
